import React from 'react';
import PropTypes from 'prop-types';
import { throttle } from 'lodash';
import cx from 'classnames';

const HEADER_HEIGHT = 70;
const DATE_PICKER_HEIGHT = 48;

export default class StickyTableHeaderHelper extends React.PureComponent {
    static propTypes = {
        children: PropTypes.node.isRequired,
        scrollingContainerId: PropTypes.string.isRequired,
        navElement: PropTypes.node,
        extraClassName: PropTypes.string
    };

    static defaultProps = {
        navElement: undefined,
        extraClassName: undefined
    };

    constructor(props) {
        super(props);

        this.state = {
            isStuck: false,
            scrollPosition: 0,
            bottom: 0
        }

        /** The page header (with rec.goc logo). Needed because it's sticky and
         * sometimes there's sticky alerts under it */
        this.header = undefined;

        /** The left side of the page that vertically scrolls */
        this.container = undefined;

        /** The div that wraps any sticky items above the table (i.e. the date
         * nav), the StickyTableHeaderHelp and the table itself */
        this.stickyTableWrap = undefined;

        /** The wrapper around just the StickyTableHeaderHelper and the table.
         * Used to handle simulated horizontal scrolling for the sticky table
         * header */
        this.horizontalScroller = undefined;

        /** If there are sticky alerts under the header */
        this.hasAlerts = false;
        this.headerAlertsBottom = 0;
        this.throttledSetSticky = throttle(this.setSticky, 250);
        this.isDesktop = window.screen.width > 767;
        this.timeout = null;
    }

    componentDidMount() {
        this.stickyTableWrap = document.getElementById('sticky-table-wrap');
        this.horizontalScroller = document.getElementById('sticky-table-horizontal-scroller');
        this.container = document.getElementById(this.props.scrollingContainerId);
        this.header = document.querySelector('header.nav-header-container');

        window.addEventListener('resize', this.throttledSetSticky);
        document.addEventListener('scroll', this.setSticky);

        if (this.container && this.isDesktop) {
            this.container.addEventListener('scroll', this.setSticky);
        }

        if (this.horizontalScroller) {
            this.horizontalScroller.addEventListener('scroll', this.handleWrapperScroll);
        }

        this.setSticky();
    }

    componentDidUpdate() {
        // If the user clicks the "Expand Table" button, the action bar may resize,
        // so we're waiting an arbitrary amount of time for the page the re-render
        // and then calling setSticky() to recalculate headerAlertsBottom

        if (this.timeout) {
            clearTimeout(this.timeout);
        }
        this.timeout = setTimeout(() => {
            this.setSticky();
        }, 300);
    }

    componentWillUnmount() {
        clearTimeout(this.timeout);

        document.removeEventListener('scroll', this.setSticky);
        window.removeEventListener('resize', this.throttledSetSticky);

        if (this.container && this.isDesktop) {
            this.container.removeEventListener('scroll', this.setSticky);
        }

        if (this.horizontalScroller) {
            this.horizontalScroller.removeEventListener('scroll', this.handleWrapperScroll);
        }
    }

    setSticky = () => {
        if (!this.stickyTableWrap) {
            return;
        }

        this.isDesktop = window.screen.width > 767;

        // We only care about the alert position if the device can display the left-right
        // container split
        if (this.isDesktop) {
            const headerBottom = this.header ? this.header.getBoundingClientRect().bottom : 0;
            let headerAlertsBottom = 0;

            const headerAlerts = document.querySelector('.nav-header-alerts');
            if (headerAlerts) {
                headerAlertsBottom = headerAlerts.getBoundingClientRect().bottom;
            }

            const bounding = this.stickyTableWrap.getBoundingClientRect();

            const bottom = headerAlertsBottom > headerBottom ? headerAlertsBottom : headerBottom;
            const isStuck = bounding.top < bottom && bounding.bottom > bottom;

            this.setState({
                isStuck,
                bottom
            });
        }
        else {
            const bottom = this.header ? HEADER_HEIGHT : 0;
            const bounding = this.stickyTableWrap.getBoundingClientRect();
            const isStuck = bounding.top + DATE_PICKER_HEIGHT < bottom && bounding.bottom > bottom;

            this.setState({
                isStuck,
                bottom
            });
        }
    }

    handleWrapperScroll = () => {
        const scrollPosition = this.horizontalScroller.scrollLeft;
        this.setState({ scrollPosition });
    }

    render() {
        const { navElement, children, extraClassName } = this.props;
        const { scrollPosition, isStuck, bottom } = this.state;

        return (
            <div
                className={cx('sticky-table-header-helper', {
                    'sticky': isStuck,
                    [extraClassName]: extraClassName
                })}
                style={{ top: bottom }}>
                {navElement && (
                    <div className="sticky-table-header-nav-wrapper">
                        {navElement}
                    </div>
                )}

                <div className="sticky-table-header-helper-wrapper">
                    <div style={{ transform: `translate3d(-${scrollPosition}px, 0, 0)` }}>
                        {children}
                    </div>
                </div>
            </div>
        );
    }
}